import React, { Component } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import {
  EditorState,
  convertToRaw,
  convertFromRaw,
  ContentState,
  convertFromHTML,
  Modifier,
  SelectionState,
} from 'draft-js';
import { UiHelper } from '../../helpers/UiHelper';
import { ApiHelper } from '../../helpers/ApiHelper';
import './Styles.css'
import { RichTextColorPicker } from '../RichTextColorPicker/RichTextColorPicker';
import LinkifyIt from 'linkify-it';

const linkify = new LinkifyIt()
const TEMPLATE_VAR_REGEX = /\{\{[^{}]*\}\}/g

/**
 * Replacement for react-draft-wysiwyg's default linkCallback. The default runs the target through
 * linkify-it, which truncates template variables like {{contact.email}} (unbalanced braces end the URL).
 * Mask the variables with plain tokens, let linkify clean up the URL (e.g. add a missing scheme), then restore them.
 */
export function richTextLinkCallback(linkData) {
  const target = String((linkData && linkData.target) || '').trim()
  const vars = []
  const masked = target.replace(TEMPLATE_VAR_REGEX, (m) => {
    vars.push(m)
    return `azvar${vars.length - 1}x`
  })
  const matches = linkify.match(masked)
  let url = (matches && matches[0] && matches[0].url) || masked
  url = url.replace(/azvar(\d+)x/g, (m, i) => (vars[i] !== undefined ? vars[i] : m))
  return { ...linkData, target: url }
}

/**
 * Comma-separated toolbar flags, e.g. "CLEAR_FORMATTING, FORMAT_BOLD, FORMAT_ITALIC, BLOCK_FORMAT, FONT_SIZE, FONT_COLOR, BULLETS, LINK".
 * Null/blank → caller uses legacy default toolbar behavior.
 */
export function parseRichTextToolbarFlags(str) {
  if (str == null || String(str).trim() === '') return null
  const set = new Set()
  String(str).split(',').forEach(p => {
    const t = p.trim().toUpperCase()
    if (t) set.add(t)
  })
  return set.size > 0 ? set : null
}

class VariablePicker extends Component {
  constructor(props) {
    super(props)
    this.state = {
      open: false
    }
    this.dropdownRef = React.createRef()
    this.openDropdown = this.openDropdown.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  openDropdown() {
    this.setState((prevState) => ({
      open: !prevState.open
    }));
  }

  handleClickOutside(event) {
    if (this.dropdownRef.current && !this.dropdownRef.current.contains(event.target)) {
      this.setState({
        open: false
      });
    }
  }

  isAutomaticMode() {
    return this.props.automatic === true
  }

  addVariable = (value) => {
    const { editorState, onChange, insertText } = this.props
    if (insertText) {
      insertText(value)
      return
    }
    const contentState = Modifier.replaceText(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      value,
      editorState.getCurrentInlineStyle(),
    );
    onChange(EditorState.push(editorState, contentState, 'insert-characters'))
  }

  addNextVariable = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (this.props.disabled) return
    const plain = this.props.editorState.getCurrentContent().getPlainText('\n')
    const indices = (String(plain || '').match(/\{\{(\d+)\}\}/g) || [])
      .map(token => parseInt(String(token).replace(/[{}]/g, ''), 10))
      .filter(n => !Number.isNaN(n))
    const maxExisting = indices.length > 0 ? Math.max(...indices) : 0
    this.addVariable(`{{${maxExisting + 1}}}`)
  }

  render() {
    const disabled = this.props.disabled
    const label = this.props.title || 'Add Variable'

    if (this.isAutomaticMode()) {
      return (
        <div className='rdw-block-wrapper' aria-label='rdw-block-control'>
          <button
            type='button'
            className={css(Styles.autoVarButton, disabled && Styles.autoVarButtonDisabled)}
            title={label}
            onMouseDown={this.addNextVariable}
            disabled={disabled}
          >
            <span className={css(Styles.autoVarButtonLabelWithPlus)}>
              <span className={css(Styles.autoVarButtonPlus)} aria-hidden>+</span>
              <span className={css(Styles.autoVarButtonText)}>{label}</span>
            </span>
          </button>
        </div>
      )
    }

    if (!this.isAutomaticMode() && this.props.options && this.props.options.length > 0) {
      const dropdownTitle = this.props.title || 'Custom Fields'
      return (
        <div ref={this.dropdownRef} onClick={this.openDropdown} className='rdw-block-wrapper' aria-label='rdw-block-control'>
          <div className={`rdw-dropdown-wrapper rdw-block-dropdown ${css(Styles.variableDropdown)}`} aria-label='rdw-dropdown'>
            <div className='rdw-dropdown-selectedtext' title={dropdownTitle}>
              <span>{dropdownTitle}</span>
              <div className={`rdw-dropdown-caretto${this.state.open ? 'close' : 'open'}`}></div>
            </div>
            <ul className={`rdw-dropdown-optionwrapper ${css(this.state.open ? Styles.variableList : Styles.variableListHidden)}`}>
              {this.props.options.map(item => (
                <li
                  onMouseDown={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    this.addVariable(item.value)
                  }}
                  key={item.key}
                  className={`rdw-dropdownoption-default ${css(Styles.variableListItem)}`}
                >{item.text}</li>
              ))}
            </ul>
          </div>
        </div>
      );
    }
    return ''
  }
}

export class RichtextEditor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      editorState: this.processValue(props.value),
      fetchState: ApiHelper.State.LOADING
    }
    this.defaultContent = null
  }

  componentDidMount() {
    this.setState({
      fetchState: ApiHelper.State.READY
    })
  }

  processValue = (value) => {
    if (value) {
      try {
        this.defaultContent = JSON.parse(value).__azformatted;
      } catch (e) {
        console.log('Json parse error', e)
        return EditorState.createEmpty()
      }
    }
    if (this.defaultContent) {
      return EditorState.createWithContent(convertFromRaw(this.defaultContent))
    } else {
      if(this.props.current.subType === 'h1') {
        return EditorState.createWithContent(
          ContentState.createFromBlockArray(
            convertFromHTML('<h1></h1>')
          )
        )
      } else if(this.props.current.subType === 'h2') {
        return EditorState.createWithContent(
          ContentState.createFromBlockArray(
            convertFromHTML('<h2></h2>')
          )
        )
      }
      return EditorState.createEmpty()
    }
  }

  onEditorStateChange = (editorState) => {
    this.setState({ editorState });
    const data = convertToRaw(editorState.getCurrentContent())
    if(data && data.blocks && data.blocks.length === 1 && data.blocks[0].text === '') {
      return this.props.handleRichtextInputChange('', this.props.current.key)
    }

    this.props.handleRichtextInputChange(JSON.stringify({ __azformatted: data }), this.props.current.key)
  };

  insertTokenAtCursor = (value) => {
    const editorState = this.state.editorState
    if (!editorState || !value) return
    const currentContent = editorState.getCurrentContent()
    let selection = editorState.getSelection()
    if (!selection.getHasFocus()) {
      const lastBlock = currentContent.getLastBlock()
      const endOffset = lastBlock ? lastBlock.getLength() : 0
      const blockKey = lastBlock ? lastBlock.getKey() : selection.getStartKey()
      selection = SelectionState.createEmpty(blockKey).merge({
        anchorOffset: endOffset,
        focusOffset: endOffset,
        hasFocus: true,
      })
    }
    const contentState = Modifier.replaceText(
      currentContent,
      selection,
      value,
      editorState.getCurrentInlineStyle(),
    )
    const nextEditorState = EditorState.push(editorState, contentState, 'insert-characters')
    this.onEditorStateChange(nextEditorState)
  }

  handleReturn = (event, editorState)=>{
    if(this.props.current.subType === 'h1' || this.props.current.subType === 'h2') return 'handled'
  }

  /*uploadImage = (file) => {
    const error = null
    return new Promise((resolve, reject) => {
      if (error) reject(error);
      else resolve({ data: { link: 'TODO:link to image' } });
    });
  };

  processValueV2 = () => {
    const c = localStorage.getItem('contentData')
    if (c) {
      this.defaultContent = JSON.parse(c)
    }
    if (this.defaultContent) {
      return EditorState.createWithContent(convertFromRaw(this.defaultContent))
    } else {
      return EditorState.createEmpty()
    }
  }

  onEditorStateChangeV2 = (editorState) => {
    this.setState({
      editorState,
    });
    const data = convertToRaw(editorState.getCurrentContent())
    localStorage.setItem('contentData', JSON.stringify(data))
  };*/

  setEditorRef(ref) {
    this.editorReferece = ref;
  }
  editorFoucs() {
    if (this.editorReferece) {
      this.editorReferece.focus();
    }
  }

  render() {
    if (this.state.fetchState === ApiHelper.State.LOADING) {
      return UiHelper.loadingView();
    } else if (this.state.fetchState === ApiHelper.State.ERROR) {
      return UiHelper.errorView(this);
    } else {
      return this.readyView();
    }
  }

  handlePastedText = (text, html, editorState) => {
    if (this.props.stripPastedImages && html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      // Many email clients render emojis as <img alt="🙂" ...>. Preserve alt text before stripping images.
      doc.querySelectorAll('img').forEach(img => {
        const alt = img.getAttribute('alt') || img.getAttribute('aria-label') || img.getAttribute('title') || ''
        if (alt && alt.trim()) {
          img.replaceWith(doc.createTextNode(alt))
        } else {
          img.remove()
        }
      })
      // If figures remain (non-image wrappers), keep visible text only.
      doc.querySelectorAll('figure').forEach(figure => {
        const txt = (figure.textContent || '').trim()
        if (txt) {
          figure.replaceWith(doc.createTextNode(txt))
        } else {
          figure.remove()
        }
      })
      const modifiedHtml = doc.body.innerHTML;

      const contentState = ContentState.createFromBlockArray(
        convertFromHTML(modifiedHtml)
      );

      const newBlockMap = contentState.getBlockMap();
      const currentContent = editorState.getCurrentContent();
      const newContentState = Modifier.replaceWithFragment(
        currentContent,
        editorState.getSelection(),
        newBlockMap
      );

      const newEditorState = EditorState.push(
        editorState,
        newContentState,
        'insert-characters'
      );

      this.onEditorStateChange(newEditorState);
      return true;
    }
    return false;
  };

  _getBlockTypeOptions() {
    const tf = parseRichTextToolbarFlags(this.props.current.toolbarFlags)
    if (tf) {
      return tf.has('BLOCK_FORMAT')
        ? ['Normal', 'H2', 'H3', 'H4', 'H5', 'H6']
        : ['Normal']
    }
    if(this.props.current.subType === 'h1') {
      return ['H1']
    } else if(this.props.current.subType === 'p') {
      return ['Normal']
    } else if(this.props.current.subType === 'h2') {
      return ['H2']
    } else  {
      return ['Normal', 'H2', 'H3', 'H4', 'H5', 'H6']
    }
  }

  _getOptions() {
    const tf = parseRichTextToolbarFlags(this.props.current.toolbarFlags)
    if (tf) {
      const opts = []
      if (tf.has('FORMAT_BOLD') || tf.has('FORMAT_ITALIC')) opts.push('inline')
      if (tf.has('BLOCK_FORMAT')) opts.push('blockType')
      if (tf.has('FONT_SIZE')) opts.push('fontSize')
      if (tf.has('FONT_COLOR')) opts.push('colorPicker')
      if (tf.has('BULLETS')) opts.push('list')
      if (tf.has('LINK')) opts.push('link')
      return opts
    }
    if(this.props.current.subType === 'h1') {
      return ['inline', 'blockType', 'fontSize','colorPicker']
    } else if(this.props.current.subType === 'h2') {
      return ['inline', 'blockType', 'fontSize','colorPicker']
    } else  {
      return ['inline', 'list', 'link', 'blockType', 'fontSize','colorPicker']
    }
  }

  _getOptionalParams() {
    const options = {}
    if(this.props.current.subType === 'h1' || this.props.current.subType === 'h2') {
      options.handleReturn = this.handleReturn
    }
    return options
  }

  readyView() {
    const tf = parseRichTextToolbarFlags(this.props.current.toolbarFlags)
    let inlineOptions = ['bold', 'italic']
    if (tf) {
      inlineOptions = []
      if (tf.has('FORMAT_BOLD')) inlineOptions.push('bold')
      if (tf.has('FORMAT_ITALIC')) inlineOptions.push('italic')
    }
    const toolbar = this.props.fullEditor ? undefined : {
      options: this._getOptions(),
      inline: {
        options: inlineOptions,
      },
      list: {
        options: ['unordered'],
      },
      blockType: {
        inDropdown: true,
        // options: ['Normal', 'H2', 'H3', 'H4', 'H5', 'H6'],
        options: this._getBlockTypeOptions()
      },
      fontSize: {
        options: [2, 4, 6, 8, 10, 12, 14, 16, 18, 20,
          22, 24, 26, 28, 30, 32, 34, 36, 38, 40,
          42, 44, 46, 48, 50, 52, 56, 60, 64, 68,
          72, 76, 80, 84, 88, 92, 96, 100
        ],
      },
      link:{
        // Marks open in new tab as default
        defaultTargetOption: '_blank',
        linkCallback: richTextLinkCallback
      },
      colorPicker: {
        component: (componentProps)=>{
          return <RichTextColorPicker
            {...componentProps}
            colorPickerData={this.props.colorPickerData}
            handleColorChange={this.props.handleColorChange}
            currentKey={this.props.current.key}
            options={{customIconRender:'format_color_text'}}
          />
        }
      },
      remove: { icon: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iMjRweCIgdmlld0JveD0iMCAtOTYwIDk2MCA5NjAiIHdpZHRoPSIyNHB4IiBmaWxsPSIjMDAwIj48cGF0aCBkPSJtNTI4LTU0Ni05My05My0xMjEtMTIxaDQ4NnYxMjBINTY4bC00MCA5NFpNNzkyLTU2IDQ2MC0zODhsLTgwIDE4OEgyNDlsMTE5LTI4MEw1Ni03OTJsNTYtNTYgNzM2IDczNi01NiA1NloiLz48L3N2Zz4=', className: undefined, title: 'Clear Formatting' },
    }
    if (this.props.colorPicker && !tf) { toolbar.options.push('colorPicker') }
    if (this.props.clearFormatting) { toolbar.options.push('remove') }
    const optionalParams = this._getOptionalParams()

    const toolbarCustomButtons = []
    const vp = this.props.variablePicker
    if (vp) {
      const automatic = vp.automatic === true
      const hasDropdown = vp.options && vp.options.length > 0
      if (automatic || hasDropdown) {
        toolbarCustomButtons.push(
          <VariablePicker
            key='variablePicker'
            {...vp}
            onChange={this.onEditorStateChange.bind(this)}
            editorState={this.state.editorState}
            insertText={this.insertTokenAtCursor.bind(this)}
            disabled={this.props.disabled || vp.disabled}
          />
        )
      }
    }

    return (

      <div className={css(Styles.container)} onClick={this.editorFoucs.bind(this)}
        style={{
          '--noLines': (this.props.current && this.props.current.rows) || 2
        }}>
        <Editor
          editorState={this.state.editorState}
          wrapperClassName='wrapper'
          editorClassName='editor'
          toolbarClassName='toolbar'
          onEditorStateChange={this.onEditorStateChange}
          toolbar={toolbar}
          stripPastedStyles={'stripPastedStyles' in this.props ? this.props.stripPastedStyles : true}
          handlePastedText={this.handlePastedText}
          editorRef={this.setEditorRef.bind(this)}
          readOnly={this.props.disabled}
          toolbarCustomButtons={toolbarCustomButtons}
          {...optionalParams}
        />
      </div>
    );
  }
}

const Styles = StyleSheet.create({
  container: {
    position:'relative'
  },
  variableDropdown: {
    width: 190,
  },
  variableList: {
    width: 190,
  },
  variableListHidden: {
    visibility: 'hidden',
  },
  variableListItem: {
    ':hover': { background: '#F1F1F1'
    }
  },
  /** Toolbar “add variable” control — only when variablePicker.automatic is true */
  autoVarButton: {
    boxSizing: 'border-box',
    margin: 0,
    padding: '5px 12px',
    minHeight: 28,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'inherit',
    fontSize: 13,
    lineHeight: 1.25,
    color: '#333',
    backgroundColor: '#fff',
    border: '1px solid #E5E5E5',
    borderRadius: 4,
    cursor: 'pointer',
    boxShadow: '0 1px 0 rgba(0,0,0,0.04)',
    WebkitFontSmoothing: 'antialiased',
    ':hover': {
      backgroundColor: '#f5f5f5',
      borderColor: '#d8d8d8',
    },
  },
  autoVarButtonDisabled: {
    opacity: 0.55,
    cursor: 'not-allowed',
    ':hover': {
      backgroundColor: '#fff',
      borderColor: '#E5E5E5',
    },
  },
  autoVarButtonLabelWithPlus: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    color: 'inherit',
  },
  autoVarButtonPlus: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1,
    color: 'inherit',
  },
  autoVarButtonText: {
    fontWeight: 500,
    color: 'inherit',
  },
})
